/**
 * AdminPage.tsx - Admin user & balance management (website_api)
 */

import { useCallback, useEffect, useState } from "react";
import TurnstileWidget from "../components/TurnstileWidget";
import AnalysisPage from "./AnalysisPage";
import ContentCachePage from "./ContentCachePage";
import DataResourcesPage from "./DataResourcesPage";
import {
  adminCreateUser,
  adminDeleteUser,
  adminLogin,
  adminRechargeUser,
  adminSetMember,
  adminSetPassword,
  fetchCaptchaConfig,
  adminSetup,
  clearStoredAdminToken,
  getAdminOverview,
  getAdminSetupStatus,
  getAdminUser,
  getAdminUserRecords,
  getAdminUserRechargeRecords,
  getStoredAdminToken,
  listAdminUsers,
  type AdminBillingRecord,
  type AdminOverview,
  type AdminUser,
  type AdminUserDetail,
  type UserRechargeRecord,
} from "../api/adminClient";
import InferenceLogsPage from "./InferenceLogsPage";
import RechargePage from "./RechargePage";
import CloudSettingsPage from "./CloudSettingsPage";
import ClientConfigPage from "./ClientConfigPage";
import AnnouncementsPage from "./AnnouncementsPage";

type AdminView =
  | "users"
  | "analysis"
  | "content"
  | "logs"
  | "resources"
  | "recharges"
  | "settings"
  | "client-config"
  | "announcements";


const inputClass =
  "w-full px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] placeholder:text-[var(--color-outline)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)] transition-colors transition-shadow duration-150";

function fmtCNY(n: number | undefined | null) {
  return `¥${(n ?? 0).toFixed(2)}`;
}

const ADMIN_RECHARGE_TYPES = [
  "系统赠送",
  "手动充值",
  "补偿充值",
  "活动赠送",
  "测试充值",
] as const;

function MetricCard({
  label,
  value,
  sub,
  index,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  index: number;
}) {
  return (
    <div
      className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)] card-hover animate-card-enter"
      style={{ "--stagger-index": index } as React.CSSProperties}
    >
      <div className="text-[13px] font-medium text-[var(--color-on-surface-variant)] mb-2">
        {label}
      </div>
      <div className="text-2xl font-semibold text-[var(--color-on-surface)] tabular-nums">
        {value}
      </div>
      {sub && (
        <div className="mt-2 text-xs text-[var(--color-on-surface-variant)]">{sub}</div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [accountInput, setAccountInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [confirmPasswordInput, setConfirmPasswordInput] = useState("");
  const [setupTokenInput, setSetupTokenInput] = useState("");
  const [setupAvailable, setSetupAvailable] = useState(false);
  const [setupChecked, setSetupChecked] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(!!getStoredAdminToken());
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [captchaSiteKey, setCaptchaSiteKey] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileResetKey((key) => key + 1);
  }, []);

  const captchaReady = !captchaEnabled || !!turnstileToken;

  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [records, setRecords] = useState<AdminBillingRecord[]>([]);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPages, setRecordsPages] = useState(1);
  const [recordTab, setRecordTab] = useState<"billing" | "recharge">("billing");
  const [rechargeRecords, setRechargeRecords] = useState<UserRechargeRecord[]>([]);
  const [rechargePage, setRechargePage] = useState(1);
  const [rechargePages, setRechargePages] = useState(1);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [rechargeAmount, setRechargeAmount] = useState("10");
  const [rechargeType, setRechargeType] = useState<string>(ADMIN_RECHARGE_TYPES[0]);
  const [rechargeNote, setRechargeNote] = useState("");
  const [showRechargeConfirm, setShowRechargeConfirm] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [adminView, setAdminView] = useState<AdminView>("users");

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserPasswordConfirm, setNewUserPasswordConfirm] = useState("");

  useEffect(() => {
    if (authenticated) return;
    getAdminSetupStatus()
      .then((s) => setSetupAvailable(s.setup_available))
      .catch(() => {
        setSetupAvailable(false);
        setLoginError('无法连接管理服务器，请稍后刷新重试');
      })
      .finally(() => setSetupChecked(true));
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) return;
    fetchCaptchaConfig()
      .then((cfg) => {
        setCaptchaEnabled(cfg.enabled && !!cfg.site_key);
        setCaptchaSiteKey(cfg.site_key || "");
      })
      .catch(() => {});
  }, [authenticated]);

  const handleLogin = async () => {
    const account = accountInput.trim();
    const password = passwordInput;
    if (!account || !password) {
      setLoginError("请输入账号和密码");
      return;
    }
    if (setupAvailable && password !== confirmPasswordInput) {
      setLoginError("两次输入的密码不一致");
      return;
    }
    if (!captchaReady) {
      setLoginError("请完成人机验证");
      return;
    }
    setLoginLoading(true);
    setLoginError(null);
    try {
      if (setupAvailable) {
        await adminSetup(account, password, setupTokenInput, turnstileToken ?? undefined);
      } else {
        await adminLogin(account, password, turnstileToken ?? undefined);
      }
      setAuthenticated(true);
      setAccountInput("");
      setPasswordInput("");
      setConfirmPasswordInput("");
      setPage(1);
      resetTurnstile();
    } catch (e: unknown) {
      setLoginError(e instanceof Error ? e.message : String(e));
      resetTurnstile();
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    clearStoredAdminToken();
    setAuthenticated(false);
    setOverview(null);
    setUsers([]);
    setSelectedId(null);
    setDetail(null);
  };

  const loadOverview = useCallback(async () => {
    try {
      const data = await getAdminOverview();
      setOverview(data);
    } catch {
      setOverview(null);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setError(null);
    try {
      const data = await listAdminUsers({ page, limit: 50, q: search });
      setUsers(data.items ?? []);
      setTotal(data.total ?? 0);
      setPages(data.pages ?? 1);
    } catch (e: unknown) {
      setUsers([]);
      setError(e instanceof Error ? e.message : String(e));
      if (String(e).includes("401") || String(e).includes("403") || String(e).includes("管理员")) {
        handleLogout();
      }
    } finally {
      setUsersLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    if (!authenticated || adminView !== "users") return;
    void loadOverview();
  }, [authenticated, adminView, loadOverview]);

  useEffect(() => {
    if (!authenticated || adminView !== "users") return;
    void loadUsers();
  }, [authenticated, adminView, loadUsers]);

  const openDetail = async (userId: number) => {
    setSelectedId(userId);
    setDetail(null);
    setRecords([]);
    setRechargeRecords([]);
    setDetailError(null);
    setActionMessage(null);
    setNewUserPassword("");
    setNewUserPasswordConfirm("");
    setRecordsPage(1);
    setRechargePage(1);
    setRecordTab("billing");
    setShowRechargeConfirm(false);
    setDetailLoading(true);
    try {
      const data = await getAdminUser(userId);
      setDetail(data);
      const rec = await getAdminUserRecords(userId, 1, 20);
      setRecords(rec.records);
      setRecordsPages(rec.pages);

      const rech = await getAdminUserRechargeRecords(userId, 1, 20);
      setRechargeRecords(rech.records);
      setRechargePages(rech.pages);
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setActionMessage(null);
    setShowRechargeConfirm(false);
  };

  const buildRechargeDescription = () => {
    const note = rechargeNote.trim();
    return note ? `${rechargeType}：${note}` : rechargeType;
  };

  const openRechargeConfirm = () => {
    if (!selectedId) return;
    const amount = parseFloat(rechargeAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setDetailError("请输入有效充值金额");
      setShowRechargeConfirm(false);
      return;
    }
    if (!rechargeType.trim()) {
      setDetailError("请选择充值类型");
      setShowRechargeConfirm(false);
      return;
    }
    setDetailError(null);
    setShowRechargeConfirm(true);
  };

  const loadRecords = async (userId: number, p: number) => {
    try {
      const rec = await getAdminUserRecords(userId, p, 20);
      setRecords(rec.records);
      setRecordsPage(rec.page);
      setRecordsPages(rec.pages);
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : String(e));
    }
  };

  const loadRechargeRecords = async (userId: number, p: number) => {
    try {
      const rech = await getAdminUserRechargeRecords(userId, p, 20);
      setRechargeRecords(rech.records);
      setRechargePage(rech.page);
      setRechargePages(rech.pages);
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleRecharge = async () => {
    if (!selectedId) return;
    const amount = parseFloat(rechargeAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setDetailError("请输入有效充值金额");
      return;
    }
    const description = buildRechargeDescription();
    setSaving(true);
    setDetailError(null);
    setActionMessage(null);
    try {
      await adminRechargeUser(selectedId, amount, description);
      setActionMessage(`已充值 ${fmtCNY(amount)}（${description}）`);
      setShowRechargeConfirm(false);
      setRechargeNote("");
      const data = await getAdminUser(selectedId);
      setDetail(data);
      await loadOverview();
      await loadUsers();
      await loadRecords(selectedId, 1);
      await loadRechargeRecords(selectedId, 1);
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMember = async () => {
    if (!selectedId || !detail) return;
    setSaving(true);
    setDetailError(null);
    setActionMessage(null);
    try {
      const next = !detail.user.is_member;
      const res = await adminSetMember(selectedId, next);
      setDetail({ ...detail, user: res.user });
      setActionMessage(next ? "已设为会员" : "已取消会员");
      await loadUsers();
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async () => {
    const username = newUsername.trim();
    const password = newPassword;
    if (!username || !password) {
      setError("请输入用户名和密码");
      return;
    }
    if (password.length < 6) {
      setError("密码至少 6 位");
      return;
    }
    setSaving(true);
    setError(null);
    setActionMessage(null);
    try {
      await adminCreateUser({
        username,
        password,
        email: newEmail.trim() || undefined,
      });
      setShowCreateForm(false);
      setNewUsername("");
      setNewPassword("");
      setNewEmail("");
      setActionMessage(`已创建用户 ${username}`);
      setPage(1);
      await loadOverview();
      await loadUsers();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async () => {
    if (!selectedId) return;
    if (!newUserPassword || newUserPassword.length < 6) {
      setDetailError("新密码至少 6 位");
      return;
    }
    if (newUserPassword !== newUserPasswordConfirm) {
      setDetailError("两次输入的密码不一致");
      return;
    }
    setSaving(true);
    setDetailError(null);
    setActionMessage(null);
    try {
      await adminSetPassword(selectedId, newUserPassword);
      setNewUserPassword("");
      setNewUserPasswordConfirm("");
      setActionMessage("密码已更新");
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedId || !detail) return;
    const name = detail.user.username;
    if (!window.confirm(`确定删除用户「${name}」？此操作不可恢复。该用户若正在使用客户端，将在下次验证时被强制下线。`)) return;
    setSaving(true);
    setDetailError(null);
    setActionMessage(null);
    try {
      await adminDeleteUser(selectedId);
      closeDetail();
      setActionMessage(`已删除用户 ${name}`);
      await loadOverview();
      await loadUsers();
    } catch (e: unknown) {
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (!authenticated) {
    if (!setupChecked) {
      return (
        <div className="animate-page-enter max-w-md mx-auto mt-16 text-center text-[var(--color-on-surface-variant)]">
          连接服务器中...
        </div>
      );
    }

    return (
      <div className="animate-page-enter max-w-md mx-auto mt-16">
        <div className="bg-[var(--color-surface)] rounded-2xl p-8 border border-[var(--color-border-subtle)]">
          <h1 className="text-xl font-semibold text-[var(--color-on-surface)] mb-2">
            {setupAvailable ? "初始化管理员" : "管理员登录"}
          </h1>
          <p className="text-sm text-[var(--color-on-surface-variant)] mb-6">
            {setupAvailable
              ? "云服务器首次使用，请创建管理员账号"
              : "使用管理员账号和密码登录"}
          </p>
          {loginError && (
            <div className="mb-4 p-3 bg-[var(--color-error-container)] text-[var(--color-on-error-container)] rounded-lg text-sm">
              {loginError}
            </div>
          )}
          <input
            type="text"
            value={accountInput}
            onChange={(e) => setAccountInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            placeholder="管理员账号"
            autoComplete="username"
            className={`${inputClass} mb-3`}
          />
          <input
            type="password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            placeholder="密码"
            autoComplete={setupAvailable ? "new-password" : "current-password"}
            className={`${inputClass}${setupAvailable ? " mb-3" : ""}`}
          />
          {setupAvailable && (
            <>
              <input
                type="password"
                value={confirmPasswordInput}
                onChange={(e) => setConfirmPasswordInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                placeholder="确认密码"
                autoComplete="new-password"
                className={`${inputClass} mb-3`}
              />
              <input
                type="password"
                value={setupTokenInput}
                onChange={(e) => setSetupTokenInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                placeholder="Setup Token（生产环境必填，本地 dev 可留空）"
                autoComplete="off"
                className={inputClass}
              />
            </>
          )}
          {captchaEnabled && captchaSiteKey && (
            <div className="mb-3">
              <TurnstileWidget
                siteKey={captchaSiteKey}
                onToken={setTurnstileToken}
                resetKey={turnstileResetKey}
              />
            </div>
          )}
          <button
            type="button"
            onClick={handleLogin}
            disabled={loginLoading || !captchaReady}
            className="mt-4 w-full px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-50"
          >
            {loginLoading
              ? setupAvailable
                ? "创建中..."
                : "登录中..."
              : setupAvailable
                ? "创建并进入"
                : "进入管理后台"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-page-enter">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <nav className="flex gap-1 p-1 bg-[var(--color-surface-container-low)] rounded-xl w-fit">
          <button
            type="button"
            onClick={() => setAdminView("users")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "users"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            用户管理
          </button>
          <button
            type="button"
            onClick={() => setAdminView("analysis")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "analysis"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            用量分析
          </button>
          <button
            type="button"
            onClick={() => setAdminView("content")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "content"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            数据资产
          </button>
          <button
            type="button"
            onClick={() => setAdminView("logs")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "logs"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            处理记录
          </button>
          <button
            type="button"
            onClick={() => setAdminView("resources")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "resources"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            数据资源库
          </button>
          <button
            type="button"
            onClick={() => setAdminView("recharges")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "recharges"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            充值记录
          </button>
          <button
            type="button"
            onClick={() => setAdminView("settings")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "settings"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            云端配置
          </button>
          <button
            type="button"
            onClick={() => setAdminView("client-config")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "client-config"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            客户端配置
          </button>
          <button
            type="button"
            onClick={() => setAdminView("announcements")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              adminView === "announcements"
                ? "bg-[var(--color-surface)] text-[var(--color-on-surface)] shadow-sm"
                : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
            }`}
          >
            公告管理
          </button>
        </nav>
        <button
          onClick={handleLogout}
          className="px-4 py-2 rounded-lg text-sm font-medium text-[var(--color-on-surface-variant)] hover:bg-[var(--color-surface-container-low)] self-start sm:self-auto"
        >
          退出
        </button>
      </div>

      {adminView === "analysis" ? (
        <AnalysisPage onAuthError={handleLogout} />
      ) : adminView === "content" ? (
        <ContentCachePage onAuthError={handleLogout} />
      ) : adminView === "logs" ? (
        <InferenceLogsPage onAuthError={handleLogout} />
      ) : adminView === "resources" ? (
        <DataResourcesPage onAuthError={handleLogout} />
      ) : adminView === "recharges" ? (
        <RechargePage onAuthError={handleLogout} />
      ) : adminView === "settings" ? (
        <CloudSettingsPage onAuthError={handleLogout} />
      ) : adminView === "client-config" ? (
        <ClientConfigPage onAuthError={handleLogout} />
      ) : adminView === "announcements" ? (
        <AnnouncementsPage onAuthError={handleLogout} />
      ) : (
        <>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1
            className="text-[28px] font-semibold text-[var(--color-on-surface)] mb-1"
            style={{ letterSpacing: "-0.02em" }}
          >
            用户管理
          </h1>
          <p className="text-[15px] text-[var(--color-on-surface-variant)]">
            查看所有用户状态与余额，支持手动创建、删除账号与修改密码
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setShowCreateForm((v) => !v);
              setError(null);
            }}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white"
          >
            {showCreateForm ? "取消" : "新建用户"}
          </button>
          <button
            onClick={() => {
              loadOverview();
              loadUsers();
            }}
            disabled={usersLoading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-surface-container-low)] text-[var(--color-on-surface)]"
          >
            {usersLoading ? "刷新中..." : "刷新"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-[var(--color-error-container)] text-[var(--color-on-error-container)] rounded-xl text-sm">
          {error}
        </div>
      )}

      {actionMessage && adminView === "users" && selectedId === null && (
        <div className="mb-6 p-4 bg-emerald-500/15 text-emerald-700 rounded-xl text-sm">
          {actionMessage}
        </div>
      )}

      {showCreateForm && (
        <div className="mb-6 bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)]">
          <h2 className="text-lg font-semibold mb-4">新建用户</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="用户名"
              className={inputClass}
            />
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="邮箱（可选）"
              className={inputClass}
            />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="密码（至少 6 位）"
              autoComplete="new-password"
              className={inputClass}
            />
          </div>
          <button
            type="button"
            onClick={handleCreateUser}
            disabled={saving}
            className="mt-4 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-50"
          >
            {saving ? "创建中..." : "创建用户"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          index={0}
          label="总用户数"
          value={overview?.total_users?.toLocaleString() ?? "—"}
        />
        <MetricCard
          index={1}
          label="总余额"
          value={overview ? fmtCNY(overview.total_balance_cny) : "—"}
          sub={
            overview
              ? `含免费额度 ${fmtCNY(overview.total_free_quota_cny)}`
              : undefined
          }
        />
        <MetricCard
          index={2}
          label="累计消费"
          value={overview ? fmtCNY(overview.total_spent_cny) : "—"}
        />
        <MetricCard
          index={3}
          label="30 日活跃"
          value={overview?.active_users_30d?.toLocaleString() ?? "—"}
          sub={
            overview
              ? `累计充值 ${fmtCNY(overview.total_recharged_cny)}`
              : undefined
          }
        />
      </div>

      <div className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)]">
        <form
          className="flex gap-2 mb-5 max-w-md"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput);
            setPage(1);
          }}
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="搜索用户名或邮箱..."
            className={inputClass}
          />
          <button
            type="submit"
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white shrink-0"
          >
            搜索
          </button>
        </form>

        {usersLoading ? (
          <div className="py-16 text-center text-[var(--color-on-surface-variant)]">
            加载用户列表...
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-[var(--color-on-surface-variant)]">
            暂无用户
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--color-on-surface-variant)] border-b border-[var(--color-border-subtle)]">
                  <th className="pb-3 pr-4 font-medium">用户</th>
                  <th className="pb-3 pr-4 font-medium hidden md:table-cell">邮箱</th>
                  <th className="pb-3 pr-4 font-medium">余额</th>
                  <th className="pb-3 pr-4 font-medium hidden lg:table-cell">消费</th>
                  <th className="pb-3 pr-4 font-medium">状态</th>
                  <th className="pb-3 font-medium hidden lg:table-cell">会员</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => openDetail(u.id)}
                    className="border-b border-[var(--color-border-subtle)] cursor-pointer hover:bg-[var(--color-surface-container-low)]"
                  >
                    <td className="py-3 pr-4">
                      <div className="font-medium flex items-center gap-2">
                        {u.username}
                        {u.is_admin && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700">
                            管理员
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[var(--color-outline)]">#{u.id}</div>
                    </td>
                    <td className="py-3 pr-4 hidden md:table-cell text-[var(--color-on-surface-variant)]">
                      {u.email || "—"}
                    </td>
                    <td className="py-3 pr-4 tabular-nums">{fmtCNY(u.total_available)}</td>
                    <td className="py-3 pr-4 hidden lg:table-cell tabular-nums">
                      {fmtCNY(u.total_spent)}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          u.allowed
                            ? "bg-emerald-500/15 text-emerald-600"
                            : "bg-amber-500/15 text-amber-700"
                        }`}
                      >
                        {u.allowed ? "可用" : "余额不足"}
                      </span>
                    </td>
                    <td className="py-3 hidden lg:table-cell">
                      {u.is_member ? "是" : "否"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-[var(--color-border-subtle)]">
            <span className="text-sm text-[var(--color-on-surface-variant)]">
              共 {total} 人 · 第 {page}/{pages} 页
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
              >
                上一页
              </button>
              <button
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedId !== null && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={closeDetail} aria-hidden />
          <div className="relative w-full max-w-xl h-full bg-[var(--color-surface)] border-l border-[var(--color-border-subtle)] shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface)]">
              <h2 className="text-lg font-semibold">用户详情</h2>
              <button
                onClick={closeDetail}
                className="p-2 rounded-lg hover:bg-[var(--color-surface-container-low)]"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {detailError && (
                <div className="p-3 bg-[var(--color-error-container)] text-[var(--color-on-error-container)] rounded-lg text-sm">
                  {detailError}
                </div>
              )}
              {actionMessage && (
                <div className="p-3 bg-emerald-500/15 text-emerald-700 rounded-lg text-sm">
                  {actionMessage}
                </div>
              )}

              {detailLoading ? (
                <div className="py-12 text-center text-[var(--color-on-surface-variant)]">
                  加载中...
                </div>
              ) : detail ? (
                <>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-[var(--color-on-surface-variant)]">用户名: </span>
                      {detail.user.username}
                      {detail.user.is_admin && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700">
                          管理员
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-[var(--color-on-surface-variant)]">邮箱: </span>
                      {detail.user.email || "—"}
                    </div>
                    <div>
                      <span className="text-[var(--color-on-surface-variant)]">注册: </span>
                      {detail.user.created_at}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-xl bg-[var(--color-surface-container-low)]">
                      <div className="text-[var(--color-on-surface-variant)] text-xs">可用余额</div>
                      <div className="font-semibold tabular-nums">
                        {fmtCNY(detail.user.total_available)}
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-[var(--color-surface-container-low)]">
                      <div className="text-[var(--color-on-surface-variant)] text-xs">付费余额</div>
                      <div className="font-semibold tabular-nums">
                        {fmtCNY(detail.user.balance_cny)}
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-[var(--color-surface-container-low)]">
                      <div className="text-[var(--color-on-surface-variant)] text-xs">免费额度</div>
                      <div className="font-semibold tabular-nums">
                        {fmtCNY(detail.user.free_quota_cny)}
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-[var(--color-surface-container-low)]">
                      <div className="text-[var(--color-on-surface-variant)] text-xs">累计消费</div>
                      <div className="font-semibold tabular-nums">
                        {fmtCNY(detail.user.total_spent)}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleToggleMember}
                      disabled={saving}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-surface-container-low)] disabled:opacity-40"
                    >
                      {detail.user.is_member ? "取消会员" : "设为会员"}
                    </button>
                  </div>

                  <div>
                    <label className="block text-[13px] font-medium text-[var(--color-on-surface-variant)] mb-2">
                      修改密码
                    </label>
                    <div className="space-y-2">
                      <input
                        type="password"
                        value={newUserPassword}
                        onChange={(e) => setNewUserPassword(e.target.value)}
                        placeholder="新密码（至少 6 位）"
                        autoComplete="new-password"
                        className={inputClass}
                      />
                      <input
                        type="password"
                        value={newUserPasswordConfirm}
                        onChange={(e) => setNewUserPasswordConfirm(e.target.value)}
                        placeholder="确认新密码"
                        autoComplete="new-password"
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={handleSetPassword}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-surface-container-low)] disabled:opacity-40"
                      >
                        更新密码
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[13px] font-medium text-[var(--color-on-surface-variant)] mb-2">
                      手动充值
                    </label>
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <select
                          value={rechargeType}
                          onChange={(e) => {
                            setRechargeType(e.target.value);
                            setShowRechargeConfirm(false);
                          }}
                          className={inputClass}
                        >
                          {ADMIN_RECHARGE_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min={0.01}
                          step={0.01}
                          value={rechargeAmount}
                          onChange={(e) => {
                            setRechargeAmount(e.target.value);
                            setShowRechargeConfirm(false);
                          }}
                          placeholder="金额（CNY）"
                          className={inputClass}
                        />
                      </div>
                      <input
                        type="text"
                        value={rechargeNote}
                        onChange={(e) => {
                          setRechargeNote(e.target.value);
                          setShowRechargeConfirm(false);
                        }}
                        placeholder="备注（可选，会写入充值记录）"
                        className={inputClass}
                        maxLength={48}
                      />
                      {!showRechargeConfirm ? (
                        <button
                          type="button"
                          onClick={openRechargeConfirm}
                          disabled={saving}
                          className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-40"
                        >
                          充值
                        </button>
                      ) : (
                        <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/10 space-y-3">
                          <p className="text-sm text-[var(--color-on-surface)]">
                            确认为用户{" "}
                            <strong>{detail?.user.username ?? `#${selectedId}`}</strong>{" "}
                            充值{" "}
                            <strong className="tabular-nums">{fmtCNY(parseFloat(rechargeAmount) || 0)}</strong>
                            ？
                          </p>
                          <p className="text-xs text-[var(--color-on-surface-variant)]">
                            类型：{buildRechargeDescription()}
                          </p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleRecharge}
                              disabled={saving}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-40"
                            >
                              {saving ? "提交中..." : "确认充值"}
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowRechargeConfirm(false)}
                              disabled={saving}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-surface-container-low)] disabled:opacity-40"
                            >
                              取消
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex border-b border-[var(--color-border-subtle)] mb-3">
                      <button
                        type="button"
                        onClick={() => setRecordTab("billing")}
                        className={`pb-2 pr-4 text-xs font-semibold border-b-2 transition-all duration-150 ${
                          recordTab === "billing"
                            ? "border-[var(--color-accent-blue)] text-[var(--color-on-surface)]"
                            : "border-transparent text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
                        }`}
                      >
                        消费记录 ({detail.summary.total_requests} 次)
                      </button>
                      <button
                        type="button"
                        onClick={() => setRecordTab("recharge")}
                        className={`pb-2 pr-4 text-xs font-semibold border-b-2 transition-all duration-150 ${
                          recordTab === "recharge"
                            ? "border-[var(--color-accent-blue)] text-[var(--color-on-surface)]"
                            : "border-transparent text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]"
                        }`}
                      >
                        充值/赠送记录 ({rechargeRecords.length} 条)
                      </button>
                    </div>

                    {recordTab === "billing" ? (
                      <>
                        <h4 className="text-[11px] font-medium text-[var(--color-on-surface-variant)] mb-2">
                          消费汇总: {detail.summary.total_requests} 次 · {fmtCNY(detail.summary.total_cost)}
                        </h4>
                        {records.length === 0 ? (
                          <p className="text-sm text-[var(--color-on-surface-variant)]">暂无记录</p>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {records.map((r) => (
                              <div
                                key={r.id}
                                className="p-3 rounded-lg bg-[var(--color-surface-container-low)] text-xs"
                              >
                                <div className="flex justify-between gap-2">
                                  <span className="font-medium">
                                    {r.request_type} / {r.provider}
                                  </span>
                                  <span className="tabular-nums">{fmtCNY(r.cost_cny)}</span>
                                </div>
                                <div className="text-[var(--color-on-surface-variant)] mt-1">
                                  {r.created_at} · {r.status}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {recordsPages > 1 && selectedId && (
                          <div className="flex gap-2 mt-3">
                            <button
                              disabled={recordsPage <= 1}
                              onClick={() => loadRecords(selectedId, recordsPage - 1)}
                              className="px-3 py-1 rounded text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
                            >
                              上一页
                            </button>
                            <button
                              disabled={recordsPage >= recordsPages}
                              onClick={() => loadRecords(selectedId, recordsPage + 1)}
                              className="px-3 py-1 rounded text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
                            >
                              下一页
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {rechargeRecords.length === 0 ? (
                          <p className="text-sm text-[var(--color-on-surface-variant)]">暂无记录</p>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {rechargeRecords.map((r) => (
                              <div
                                key={r.id}
                                className="p-3 rounded-lg bg-[var(--color-surface-container-low)] text-xs"
                              >
                                <div className="flex justify-between gap-2">
                                  <span className="font-medium flex items-center gap-1.5">
                                    <span className={`w-1.5 h-1.5 rounded-full ${
                                      r.order_no?.startsWith("ADMIN_")
                                        ? "bg-violet-500"
                                        : r.order_no === "REGISTRATION_GIFT"
                                        ? "bg-emerald-500"
                                        : "bg-blue-500"
                                    }`} />
                                    {r.description}
                                  </span>
                                  <span className="tabular-nums font-semibold">{fmtCNY(r.amount_cny)}</span>
                                </div>
                                <div className="text-[var(--color-on-surface-variant)] mt-1 font-mono text-[10px] break-all">
                                  订单号: {r.order_no}
                                </div>
                                <div className="text-[var(--color-on-surface-variant)] mt-1 flex justify-between">
                                  <span>{r.created_at}</span>
                                  <span className={`font-semibold ${r.status === 'paid' ? 'text-emerald-600' : 'text-slate-500'}`}>
                                    {r.status === 'paid' ? '已支付' : r.status}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {rechargePages > 1 && selectedId && (
                          <div className="flex gap-2 mt-3">
                            <button
                              disabled={rechargePage <= 1}
                              onClick={() => loadRechargeRecords(selectedId, rechargePage - 1)}
                              className="px-3 py-1 rounded text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
                            >
                              上一页
                            </button>
                            <button
                              disabled={rechargePage >= rechargePages}
                              onClick={() => loadRechargeRecords(selectedId, rechargePage + 1)}
                              className="px-3 py-1 rounded text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
                            >
                              下一页
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="pt-4 border-t border-[var(--color-border-subtle)]">
                    <button
                      type="button"
                      onClick={handleDeleteUser}
                      disabled={saving}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-red-600 bg-red-500/10 hover:bg-red-500/15 disabled:opacity-40"
                    >
                      删除此用户
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
